<script setup lang="ts">import { ref as _ref } from 'vue';

import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
defineProps({
  value: null
});
import { watch } from 'vue';
import { getModule } from 'vuex-module-decorators';
import store from '@/store';
import authModule from '@/store/modules/auth';
import {
  AlertingHookService, AlertingHookDto, UserUiDto, UserService,
} from '@/api';
import i18n from '@/i18n';

interface Emit {
  (event: 'input', value: AlertingHookDto[]): void
}
const emit = defineEmits(["input"]);

const auth = getModule(authModule, store);

const { value: selectedMails } = __MACROS_useVModel(["value", undefined, "input"]);

let loading = _ref(false);
let mails = _ref<AlertingHookDto[]>([]);
const searchString = _ref('');

let users = _ref<UserUiDto[]>([]);

const icon = (itemAttrs: any): string => (itemAttrs.inputValue ? 'icon-check-square-fill' : 'icon-check-square-outline-empty');
const itemClass = (itemAttrs: any): string => (itemAttrs.inputValue ? 'primary--text v-list-item--active' : '');
const userName = (hook: AlertingHookDto): string | undefined => {
  const user = users.value.find((u) => u.email === hook.externalHook);
  return auth.getUser && auth.getUser.id === user?.id ? i18n.t('users.current_user_display', { user: user.name }) : user?.name;
};

const clear = () => {
  selectedMails.value = [];
  emit('input', selectedMails.value);
};

const putCurrentUserFirst = () => {
  const currentUserEmail = auth.getUser?.email;
  const currentUserIndex = mails.value.findIndex((mail) => mail.externalHook === currentUserEmail);
  if (currentUserIndex !== -1) {
    const hookIndex = mails.value.splice(currentUserIndex, 1)[0];
    mails.value.unshift(hookIndex);
  }
};

const fetchAllMailAlertingHooks = async () => {
  loading.value = true;
  const { data } = await AlertingHookService.getAllMailAlertingHooks();
  mails.value = data!;
  putCurrentUserFirst();
  loading.value = false;
};

const fetchAllUsers = async () => {
  const { data } = await UserService.getAllUsers({ });
  users.value = data!;
};

watch(() => searchString.value, () => {
  fetchAllMailAlertingHooks();
  fetchAllUsers();
});
</script>

<template lang="pug">
v-autocomplete.tag-selector(
  v-model="selectedMails"
  v-bind='$attrs'
  v-on='$listeners'
  :loading="loading"
  :items="mails"
  :search-input.sync="searchString"
  :hint="$t('monitor_wizard.notification_method.email_addresses_hint')"
  @change="searchString=''"
  @click:clear="clear"
  :placeholder="$t('common.words.type_to_search')"
  item-value="id"
  item-text="externalHook"
  data-track="mail-tag-selector"
  cache-items dense outlined
  hide-no-data persistent-hint
  return-object multiple clearable )

  template(v-slot:label)
    span {{ $t('selectors.emails_label') }}

  template(v-slot:item="{ item, on, attrs }")
    v-list-item( :class="itemClass(attrs)" ripple @mousedown.prevent v-on="on" active data-track="mail-selector-item" )
      v-list-item-action
        v-icon {{ icon(attrs) }}
      v-list-item-content
        v-list-item-title
          .d-flex.flex-column.ml-1
            .body-2.grey--text.text--darken-4 {{ item.externalHook }}
            .caption.grey--text.text--grey-secondary(v-if="item.user") {{ userName(item) }}

</template>
